import { theme } from '@luminarium-ai/luminarium-core-ui';
import { MantineProvider, v8CssVariablesResolver } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { createBrowserRouter } from 'react-router';
// v8: the DOM-optimized RouterProvider lives in the /dom subpath.
import { RouterProvider } from 'react-router/dom';
import { initBreadcrumbs } from './lib/telemetry/breadcrumb';
import { LoginPage } from './views/auth/LoginPage';
import { VerifyPage } from './views/auth/VerifyPage';
import { InterviewPage } from './views/interview/InterviewPage';
import { MicTestPage } from './views/mic-test/MicTestPage';
import { NotFoundPage } from './views/NotFoundPage';
import { ProtectedRoute } from './views/ProtectedRoute';
import { RootLayout } from './views/RootLayout';
import { RootRedirect } from './views/RootRedirect';
import { SplitLayout } from './views/SplitLayout';

/**
 * Root App component for the Vite SPA.
 *
 * Provides:
 * - MantineProvider (theme)
 * - QueryClientProvider (React Query)
 * - RouterProvider (React Router v7, data mode)
 *
 * Replaces the Next.js `src/app/layout.tsx` and `src/providers/Providers.tsx`.
 */

// Eager breadcrumb init (LUM-69): installs the page-lifecycle flush
// listeners (visibilitychange / pagehide) up front. track() would
// self-initialise lazily anyway, but events emitted before the first
// explicit init would miss the unload-flush safety net. This replaces
// the old AnalyticsBootstrap provider, which was never mounted —
// which also means Mixpanel never actually initialised in the Vite
// app, making its removal (LUM-70) a no-op for prod data.
initBreadcrumbs();

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Root is a redirect gate — mirrors the Next.js middleware behaviour.
      // Authenticated → /interview. Unauthenticated → /auth/login.
      { path: '/', element: <RootRedirect /> },
      {
        // Auth resolves before the layout mounts, so the star-led
        // in-session header never flashes for unauthenticated visitors.
        element: (
          <ProtectedRoute>
            <SplitLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: '/interview', element: <InterviewPage /> },
          { path: '/mic-test', element: <MicTestPage /> },
        ],
      },
      { path: '/auth/login', element: <LoginPage /> },
      { path: '/auth/verify', element: <VerifyPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            retry: (failureCount, error: unknown) => {
              const err = error as { status?: number };
              if (err?.status && err.status >= 400 && err.status < 500) {
                return false;
              }
              return failureCount < 3;
            },
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider
        theme={theme}
        cssVariablesResolver={v8CssVariablesResolver}
      >
        <Notifications position="top-right" />
        <RouterProvider router={router} />
      </MantineProvider>
    </QueryClientProvider>
  );
}
